import { Form, Formik } from "formik";
import {
  BACK_BUTTON_TEXT,
  NEXT_BUTTON_TEXT,
  CURRENT_COVERAGE_FORM,
  LOADING_BUTTON_TEXT,
} from "pages/PreQualification/utils/constants";
import Alert from "uicomponents/Alert";
import { useEffect, useState } from "react";
import FooterButton from "uicomponents/FooterButton";
import styles from "./CurrentCoverage.module.css";
import CurrentCoverageComponent from "./component/CurrentCoverageComponent";
import { CurrentCoverageType } from "types";
import { CurrentCoverageFormValues } from "pages/PreQualification/utils/initialFormValues";
import {
  currentCoverageValidationSchema,
  currentCoverageValidationSchemaCoreApp,
} from "pages/PreQualification/utils/validationSchema";
import { CurrentCoverageFormType } from "pages/PreQualification/utils/formTypes";
import {
  convertStringIntoBool,
  ConvertToISODate,
  errorMessageMap,
  hasQueryParam,
} from "utils/helper";
import {
  trackPageViewUserStep,
  trackSaveAndContinueUserStep,
} from "utils/analytics/trackEvent";
import {
  useUpdateCurrentCoverageMutation,
  useUpdateProspectInfoMutation,
} from "types/generated/operations";
import { getMemberUuid, getProspectUuid } from "utils/idHelpers";
import { HealthcareCarrierTypeEnum } from "types/generated/schema";
import ResultPageDeniedModel from "../Results/DeniedComponent";
import { sendCustomerIOEvent } from "utils/analytics/customerio";
import { getAppType } from "utils/helper";
import { useOktaAuth } from "@okta/okta-react";
import { trackFormError } from "utils/analytics/trackEvent";

interface ICurrentCoverageProps {
  data: CurrentCoverageType;
  setNextStep: (submitApp?: boolean, stepNumber?: number) => void;
  setPreviousStep: () => void;
  setDisableStepperValue: () => void;
  disableNextStep?: boolean;
}

const CurrentCoverage = (props: ICurrentCoverageProps) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(2, "Current coverage", 1);
  }, []);

  const {
    data,
    setNextStep,
    setPreviousStep,
    setDisableStepperValue,
    disableNextStep,
  } = props;
  const appType = getAppType();
  const { authState } = useOktaAuth();
  const accessToken = authState?.accessToken?.accessToken;

  const initialFormValues = CurrentCoverageFormValues(data);
  const [showHIDenied, setShowHIDenied] = useState(false);

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingSteps, setIsLoadingSteps] = useState(false);

  const [updateCurrentCoverage, { loading: updateCurrentCoverageLoading }] =
    useUpdateCurrentCoverageMutation();

  const [updateProspectInfo, { loading: updateProspectInfoLoading }] =
    useUpdateProspectInfoMutation();

  const hi_flow_with_core_app = hasQueryParam("usecoreapp");

  const handleSubmit = async (values: CurrentCoverageFormType) => {
    try {
      setIsLoadingSteps(true);

      if (hi_flow_with_core_app) {
        await updateProspectInfo({
          variables: {
            uuid: getProspectUuid(),
            sicCode: values.sicCode,
          },
        });
      }

      const currentCoverageResponse = await updateCurrentCoverage({
        variables: hi_flow_with_core_app
          ? {
              uuid: getProspectUuid(),
              cobraUsage: values.cobraUsage
                ? convertStringIntoBool(values.cobraUsage)
                : null,
              currentlyOfferHealthInsurance:
                values.currentlyOfferHealthInsurance
                  ? convertStringIntoBool(values.currentlyOfferHealthInsurance)
                  : null,
              currentCarrier:
                (values.currentCarrier as HealthcareCarrierTypeEnum) || null,
              currentCarrierOther: values.currentCarrierOther,
              currentRenewalDate: values.currentRenewalDate
                ? ConvertToISODate(values.currentRenewalDate)
                : null,
              cobraUsers: Number(values.cobraUsers),
              benefitEligibleEmployees: Number(values.benefitEligibleEmployees),
              controlledGroup: values.controlledGroup
                ? convertStringIntoBool(values.controlledGroup)
                : null,
              isOnPeo: values.isOnPeo
                ? convertStringIntoBool(values.isOnPeo)
                : null,
              currentPeo: values.currentPeo,
              currentPeoOther: values.currentPeoOther,
              startingCoverageDate: values.startingCoverageDate
                ? ConvertToISODate(values.startingCoverageDate)
                : null,
            }
          : {
              uuid: getProspectUuid(),
              cobraUsage: values.cobraUsage
                ? convertStringIntoBool(values.cobraUsage)
                : null,
              currentlyOfferHealthInsurance:
                values.currentlyOfferHealthInsurance
                  ? convertStringIntoBool(values.currentlyOfferHealthInsurance)
                  : null,
              currentCarrier:
                (values.currentCarrier as HealthcareCarrierTypeEnum) || null,
              currentCarrierOther: values.currentCarrierOther,
              currentRenewalDate: values.currentRenewalDate
                ? ConvertToISODate(values.currentRenewalDate)
                : null,
              cobraUsers: Number(values.cobraUsers),
              benefitEligibleEmployees: Number(values.benefitEligibleEmployees),
            },
      });
      trackSaveAndContinueUserStep(2, "Current coverage", 1);

      if (
        currentCoverageResponse.data?.updateProspectHealthInsuranceInfo
          ?.isCoverageEligible
      ) {
        sendCustomerIOEvent(
          {
            event_name: "health_insurance_application_qualified",
            event_type: "health_app_stages",
            member_uuid: getMemberUuid(),
            data: {
              health_app_status: "HI_QUALIFIED",
              app_name: appType,
              version: 1, // TODO: use flowVersion
            },
          },
          accessToken || ""
        );

        setNextStep();
      } else {
        setShowHIDenied(true);
        setDisableStepperValue();
      }
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  return (
    <div>
      {showHIDenied ? (
        <ResultPageDeniedModel />
      ) : (
        <div>
          <div className={styles.title}>{CURRENT_COVERAGE_FORM.TITLE}</div>
          <Formik
            initialValues={initialFormValues}
            validationSchema={
              hi_flow_with_core_app
                ? currentCoverageValidationSchemaCoreApp
                : currentCoverageValidationSchema
            }
            onSubmit={handleSubmit}
          >
            {() => (
              <Form>
                <CurrentCoverageComponent />
                {isError && <Alert message={errorMessage} />}
                <FooterButton
                  nextButtonText={
                    updateCurrentCoverageLoading ||
                    updateProspectInfoLoading ||
                    isLoadingSteps
                      ? LOADING_BUTTON_TEXT
                      : NEXT_BUTTON_TEXT
                  }
                  backButtonText={BACK_BUTTON_TEXT}
                  disabled={
                    updateCurrentCoverageLoading ||
                    updateProspectInfoLoading ||
                    isLoadingSteps ||
                    disableNextStep
                  }
                  handleBackClick={setPreviousStep}
                />
              </Form>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default CurrentCoverage;
